import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useBackdropDismiss from '../hooks/useBackdropDismiss';
import Icon from './Icon';

export default function Drawer({ open, onClose, title, icon, width = 420, children, footer }) {
  const { t } = useTranslation();
  const closeRef = useRef(null);
  const backdrop = useBackdropDismiss(onClose);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Focus close button on open for keyboard accessibility
    const t = setTimeout(() => closeRef.current?.focus(), 50);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="drawer-overlay"
      {...backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="drawer-panel"
        style={{ width, maxWidth: '95vw' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="drawer-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {icon && (
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: 'var(--accent-dim)',
                border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={icon} size="xs" color="var(--accent)" />
              </div>
            )}
            <h2 style={{
              margin: 0, fontSize: 14, fontWeight: 700,
              letterSpacing: '-0.01em', color: 'var(--text-primary)',
            }}>
              {title}
            </h2>
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label={t('common.a11y.close')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: '4px 6px',
              borderRadius: 6, display: 'flex', alignItems: 'center',
              transition: 'background var(--dur-fast), color var(--dur-fast)',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--overlay-subtle)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            <Icon name="close" size="sm" />
          </button>
        </div>

        {/* Body */}
        <div className="drawer-body">
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div className="drawer-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
